/**
 * The tables of a document body, read once, so every question about a
 * paragraph's place in a table is answered from one walk.
 *
 * Word nests a paragraph in a cell (`w:tc`), a cell in a row (`w:tr`), a row
 * in a table (`w:tbl`), and wraps any of the three in content controls
 * (`w:sdt/w:sdtContent`), tracked-change containers and custom XML at will.
 * `TableStructure` reads the nesting by nearest ancestor, so every wrapper
 * between a paragraph and its cell, a cell and its row, or a row and its table
 * is transparent, the way `ParagraphModel` reads through every inline wrapper
 * around a run. A nested table is a table like any other: its cells sit inside
 * an outer cell, and a paragraph inside it carries both cells on its path.
 *
 * Tables inside a text box (a `w:tbl` under a `w:p`) and inside an
 * `mc:Fallback` copy are left out, because `OoxmlRange.bodyParagraphs` leaves
 * their paragraphs out: the model never sees them, so they must not take a
 * table number.
 */
import { WORDPROCESSINGML_NS, elementNameWithoutPrefix } from "./ooxml-names";
import { ParagraphModel } from "./paragraph-model";
import type { DocumentTableCell, TableCellPath, TableRowRevisionState, VerticalMergeState } from "../wire/table-cell-path";

/** The `w:tblLook` bit Word sets when the first row is styled as a header (`w:val="04A0"` has it; `w:firstRow="1"` is the same fact spelled out). */
const TABLE_LOOK_FIRST_ROW_BIT = 0x0020;

/** One row as read: its element, the cells in order, and the row-level facts every cell of the row shares. */
export interface TableRowStructure {
    rowElement: Element;
    rowNumber: number;
    cellElements: Element[];
    isHeaderRow: boolean;
    rowRevision: TableRowRevisionState;
}

/** One table as read: its element, its rows in order, and how many grid columns it declares. */
export interface TableStructureEntry {
    tableElement: Element;
    tableNumber: number;
    rows: TableRowStructure[];
    gridColumnCount: number;
}

export class TableStructure {
    private readonly tables: TableStructureEntry[];
    private readonly cellRecordByCellElement: Map<Element, DocumentTableCell>;
    private readonly rowByRowElement: Map<Element, TableRowStructure>;
    private readonly tableByTableElement: Map<Element, TableStructureEntry>;

    constructor(bodyRoot: Element) {
        this.tables = TableStructure.readTables(bodyRoot);
        this.cellRecordByCellElement = new Map();
        this.rowByRowElement = new Map();
        this.tableByTableElement = new Map();
        for (const table of this.tables) {
            this.tableByTableElement.set(table.tableElement, table);
            for (const row of table.rows) {
                this.rowByRowElement.set(row.rowElement, row);
                for (const cellRecord of TableStructure.cellRecordsOfRow(table, row)) this.cellRecordByCellElement.set(cellRecord.cellElement, cellRecord.record);
            }
        }
    }

    /** Every table of the body in document order, nested tables included. */
    get tableEntries(): readonly TableStructureEntry[] {
        return this.tables;
    }

    /**
     * The cells a paragraph sits in, outermost first; empty for a paragraph
     * outside every table.
     *
     * EXAMPLE
     *   body > tbl#1 > tr#3 > tc#2 > tbl#2 > tr#1 > tc#1 > p
     *   -> [ {tableNumber 1, rowNumber 3, cellNumber 2}, {tableNumber 2, rowNumber 1, cellNumber 1} ]
     */
    cellPathOf(paragraph: Element): TableCellPath {
        const path: DocumentTableCell[] = [];
        for (let ancestor = paragraph.parentNode; ancestor !== null && ancestor.nodeType === 1; ancestor = ancestor.parentNode) {
            const cellRecord = this.cellRecordByCellElement.get(ancestor as Element);
            /**
             * WHEN the ancestor is not one of the indexed cells (a wrapper, a row, a table, the body)
             *   CONSEQUENCE
             *     Keep climbing; only cells contribute a path entry.
             */
            if (cellRecord === undefined) continue;
            path.unshift(cellRecord);
        }
        return path;
    }

    /** The innermost row a paragraph sits in, or null for a paragraph outside every table. */
    rowOf(paragraph: Element): TableRowStructure | null {
        for (let ancestor = paragraph.parentNode; ancestor !== null && ancestor.nodeType === 1; ancestor = ancestor.parentNode) {
            const row = this.rowByRowElement.get(ancestor as Element);
            if (row !== undefined) return row;
        }
        return null;
    }

    /** The table a row belongs to; every indexed row has one. */
    tableOfRow(row: TableRowStructure): TableStructureEntry {
        const tableElement = TableStructure.nearestAncestorNamed(row.rowElement, "tbl");
        const table = tableElement === null ? undefined : this.tableByTableElement.get(tableElement);
        /**
         * WHEN the row was read from this structure
         *   CONSEQUENCE
         *     Its table was indexed with it; a miss means the row element came from another document.
         */
        if (table === undefined) throw new Error("The row does not belong to a table of this document.");
        return table;
    }

    /** The cell record for a cell element read by this structure, or null for an element that is not one of its cells. */
    cellRecordOf(cellElement: Element): DocumentTableCell | null {
        return this.cellRecordByCellElement.get(cellElement) ?? null;
    }

    /**
     * Every `w:tbl` under the body that is not inside a paragraph (a text box)
     * or an `mc:Fallback` copy, numbered in document order, with its rows and
     * their cells grouped by nearest ancestor.
     */
    private static readTables(bodyRoot: Element): TableStructureEntry[] {
        const tableElements = Array.from(bodyRoot.getElementsByTagNameNS(WORDPROCESSINGML_NS, "tbl")).filter((tableElement) => !TableStructure.isInsideExcludedContainer(tableElement, bodyRoot));
        const rowElementsByTable = TableStructure.groupByNearestAncestor(Array.from(bodyRoot.getElementsByTagNameNS(WORDPROCESSINGML_NS, "tr")), "tbl");
        const cellElementsByRow = TableStructure.groupByNearestAncestor(Array.from(bodyRoot.getElementsByTagNameNS(WORDPROCESSINGML_NS, "tc")), "tr");
        return tableElements.map((tableElement, tableIndex) => {
            const rowElements = rowElementsByTable.get(tableElement) ?? [];
            const tableLookSaysFirstRowIsHeader = TableStructure.tableLookHasFirstRow(tableElement);
            const rows: TableRowStructure[] = rowElements.map((rowElement, rowIndex) => {
                const rowProperties = TableStructure.childNamed(rowElement, "trPr");
                const rowIsRepeatedHeader = rowProperties !== null && TableStructure.childNamed(rowProperties, "tblHeader") !== null;
                /**
                 * CASES, for whether the row is a header row
                 *   - w:trPr/w:tblHeader present                    -> yes, Word repeats it on every page
                 *   - first row and the table look styles it        -> yes, a header by look (most fee tables)
                 *   - otherwise                                      -> no
                 *
                 * EXAMPLE
                 *   rowIndex = 0, tblLook w:val="04A0" -> isHeaderRow = true
                 *   rowIndex = 1, same table           -> isHeaderRow = false unless tblHeader
                 */
                const isHeaderRow = rowIsRepeatedHeader || (rowIndex === 0 && tableLookSaysFirstRowIsHeader);
                return {
                    rowElement,
                    /**
                     * EXAMPLE
                     *   rowIndex = 0 -> rowNumber = 1, the first row the model sees as "1/6"
                     */
                    rowNumber: rowIndex + 1,
                    cellElements: cellElementsByRow.get(rowElement) ?? [],
                    isHeaderRow,
                    rowRevision: TableStructure.rowRevisionOf(rowProperties),
                };
            });
            return {
                tableElement,
                /**
                 * EXAMPLE
                 *   tableIndex = 0 -> tableNumber = 1, shown as table="1"
                 */
                tableNumber: tableIndex + 1,
                rows,
                gridColumnCount: TableStructure.gridColumnCountOf(tableElement, rows),
            };
        });
    }

    /**
     * The cell records of one row: grid positions from the row's own
     * `gridBefore` and the spans before each cell, and the column heading read
     * from the nearest header row above.
     */
    private static cellRecordsOfRow(table: TableStructureEntry, row: TableRowStructure): { cellElement: Element; record: DocumentTableCell }[] {
        const rowProperties = TableStructure.childNamed(row.rowElement, "trPr");
        /**
         * EXAMPLE
         *   w:trPr/w:gridBefore w:val="1", first cell -> gridColumnFirst = 1 + 1 = 2
         *   no gridBefore, first cell                 -> gridColumnFirst = 1
         */
        let nextGridColumn = 1 + TableStructure.integerValueOf(rowProperties === null ? null : TableStructure.childNamed(rowProperties, "gridBefore"), 0);
        const headerRow = TableStructure.headerRowAbove(table, row);
        return row.cellElements.map((cellElement, cellIndex) => {
            const cellProperties = TableStructure.childNamed(cellElement, "tcPr");
            const gridSpan = TableStructure.integerValueOf(cellProperties === null ? null : TableStructure.childNamed(cellProperties, "gridSpan"), 1);
            const gridColumnFirst = nextGridColumn;
            /**
             * EXAMPLE
             *   gridColumnFirst = 2, gridSpan = 2 -> gridColumnLast = 2 + 2 - 1 = 3, shown as columns="2-3/9"
             *   gridColumnFirst = 4, gridSpan = 1 -> gridColumnLast = 4
             */
            const gridColumnLast = gridColumnFirst + gridSpan - 1;
            nextGridColumn = gridColumnLast + 1;
            const record: DocumentTableCell = {
                tableNumber: table.tableNumber,
                rowNumber: row.rowNumber,
                rowCount: table.rows.length,
                /**
                 * EXAMPLE
                 *   cellIndex = 2 -> cellNumber = 3, shown as cell="3/3"
                 */
                cellNumber: cellIndex + 1,
                cellCount: row.cellElements.length,
                gridColumnFirst,
                gridColumnLast,
                gridColumnCount: table.gridColumnCount,
                isHeaderRow: row.isHeaderRow,
                /**
                 * CASES
                 *   - the row is itself a header row -> null, a heading has no heading
                 *   - no header row above            -> null
                 *   - otherwise                      -> the header cell covering this cell's first grid column, blank read as null
                 */
                columnHeading: row.isHeaderRow || headerRow === null ? null : TableStructure.headingOverGridColumn(headerRow, gridColumnFirst),
                verticalMerge: TableStructure.verticalMergeOf(cellProperties),
                rowRevision: row.rowRevision,
            };
            return { cellElement, record };
        });
    }

    /**
     * The nearest header row above `row`, or null when none of the rows above
     * it is one.
     *
     * EXAMPLE
     *   rows 1 and 2 are header rows, row 5 asks -> row 2
     *   row 1 asks                                 -> null (nothing above it)
     */
    private static headerRowAbove(table: TableStructureEntry, row: TableRowStructure): TableRowStructure | null {
        for (let rowIndex = row.rowNumber - 2; rowIndex >= 0; rowIndex--) {
            const candidate = table.rows[rowIndex];
            if (candidate.isHeaderRow) return candidate;
        }
        return null;
    }

    /**
     * The accepted text of the header-row cell whose grid columns cover
     * `gridColumn`, or null when no cell covers it or the cell is blank.
     *
     * EXAMPLE
     *   header cells cover columns 1, 2-3, 4; gridColumn = 3 -> the second cell's text
     */
    private static headingOverGridColumn(headerRow: TableRowStructure, gridColumn: number): string | null {
        const headerRowProperties = TableStructure.childNamed(headerRow.rowElement, "trPr");
        let nextGridColumn = 1 + TableStructure.integerValueOf(headerRowProperties === null ? null : TableStructure.childNamed(headerRowProperties, "gridBefore"), 0);
        for (const headerCellElement of headerRow.cellElements) {
            const headerCellProperties = TableStructure.childNamed(headerCellElement, "tcPr");
            const gridSpan = TableStructure.integerValueOf(headerCellProperties === null ? null : TableStructure.childNamed(headerCellProperties, "gridSpan"), 1);
            const gridColumnFirst = nextGridColumn;
            const gridColumnLast = gridColumnFirst + gridSpan - 1;
            nextGridColumn = gridColumnLast + 1;
            const coversColumn = gridColumn >= gridColumnFirst && gridColumn <= gridColumnLast;
            if (!coversColumn) continue;
            const headingText = TableStructure.acceptedTextOfCell(headerCellElement);
            return headingText.length === 0 ? null : headingText;
        }
        return null;
    }

    /** The cell's paragraphs' accepted text joined by one space, trimmed; the reading `ParagraphModel` gives every other consumer. */
    static acceptedTextOfCell(cellElement: Element): string {
        const paragraphTexts = Array.from(cellElement.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p"))
            .filter((paragraph) => TableStructure.nearestAncestorNamed(paragraph, "tc") === cellElement)
            .map((paragraph) => new ParagraphModel(paragraph).text.trim())
            .filter((text) => text.length > 0);
        return paragraphTexts.join(" ");
    }

    /**
     * The table's grid columns: the count of `w:tblGrid/w:gridCol`, or, for a
     * table that declares no grid, the widest row's span total.
     *
     * EXAMPLE
     *   tblGrid with 3 gridCol                       -> 3
     *   no tblGrid, rows spanning 2, 3 and 3 columns -> 3
     */
    private static gridColumnCountOf(tableElement: Element, rows: TableRowStructure[]): number {
        const tableGrid = TableStructure.childNamed(tableElement, "tblGrid");
        const declaredGridColumnCount = tableGrid === null ? 0 : Array.from(tableGrid.childNodes).filter((node) => node.nodeType === 1 && elementNameWithoutPrefix(node as Element) === "gridCol").length;
        if (declaredGridColumnCount > 0) return declaredGridColumnCount;
        const widestRowSpanTotal = rows.reduce((widest, row) => Math.max(widest, TableStructure.spanTotalOfRow(row)), 0);
        /**
         * WHEN the table declares no grid and has no cells
         *   CONSEQUENCE
         *     Report one column so every later division and range stays defined.
         */
        return Math.max(widestRowSpanTotal, 1);
    }

    /**
     * `gridBefore + Σ gridSpan + gridAfter` for one row: the columns it lays claim to.
     *
     * EXAMPLE
     *   gridBefore 1, cells spanning 1 and 2, gridAfter 0 -> 1 + 3 + 0 = 4
     */
    private static spanTotalOfRow(row: TableRowStructure): number {
        const rowProperties = TableStructure.childNamed(row.rowElement, "trPr");
        const gridBefore = TableStructure.integerValueOf(rowProperties === null ? null : TableStructure.childNamed(rowProperties, "gridBefore"), 0);
        const gridAfter = TableStructure.integerValueOf(rowProperties === null ? null : TableStructure.childNamed(rowProperties, "gridAfter"), 0);
        const spanSum = row.cellElements.reduce((sum, cellElement) => {
            const cellProperties = TableStructure.childNamed(cellElement, "tcPr");
            return sum + TableStructure.integerValueOf(cellProperties === null ? null : TableStructure.childNamed(cellProperties, "gridSpan"), 1);
        }, 0);
        return gridBefore + spanSum + gridAfter;
    }

    /**
     * Whether `w:tblPr/w:tblLook` styles the first row as a header, in either
     * of Word's spellings.
     *
     * CASES
     *   - w:firstRow="1"                 -> true (Word 2010+ attribute form)
     *   - w:val="04A0", bit 0x0020 set   -> true (the hex mask form)
     *   - neither, or no tblLook         -> false
     */
    private static tableLookHasFirstRow(tableElement: Element): boolean {
        const tableProperties = TableStructure.childNamed(tableElement, "tblPr");
        const tableLook = tableProperties === null ? null : TableStructure.childNamed(tableProperties, "tblLook");
        if (tableLook === null) return false;
        const firstRowAttribute = TableStructure.attributeOf(tableLook, "firstRow");
        if (firstRowAttribute !== null) return firstRowAttribute === "1" || firstRowAttribute === "true" || firstRowAttribute === "on";
        const lookMask = TableStructure.attributeOf(tableLook, "val");
        if (lookMask === null) return false;
        const parsedMask = Number.parseInt(lookMask, 16);
        return Number.isFinite(parsedMask) && (parsedMask & TABLE_LOOK_FIRST_ROW_BIT) !== 0;
    }

    /**
     * CASES
     *   - w:tcPr/w:vMerge absent                 -> "none"
     *   - w:vMerge w:val="restart"               -> "restart", the top of a merged group
     *   - w:vMerge with any other or no w:val    -> "continue", joined to the cell above
     */
    private static verticalMergeOf(cellProperties: Element | null): VerticalMergeState {
        const verticalMerge = cellProperties === null ? null : TableStructure.childNamed(cellProperties, "vMerge");
        if (verticalMerge === null) return "none";
        return TableStructure.attributeOf(verticalMerge, "val") === "restart" ? "restart" : "continue";
    }

    /**
     * CASES
     *   - w:trPr/w:ins present -> "inserted"
     *   - w:trPr/w:del present -> "deleted"
     *   - otherwise            -> "none"
     */
    private static rowRevisionOf(rowProperties: Element | null): TableRowRevisionState {
        if (rowProperties === null) return "none";
        if (TableStructure.childNamed(rowProperties, "ins") !== null) return "inserted";
        if (TableStructure.childNamed(rowProperties, "del") !== null) return "deleted";
        return "none";
    }

    /**
     * Groups elements by their nearest ancestor with `ancestorLocalName`,
     * keeping document order inside each group. An element with no such
     * ancestor (a stray `w:tc` outside any row) is left out.
     */
    private static groupByNearestAncestor(elements: Element[], ancestorLocalName: string): Map<Element, Element[]> {
        const groups = new Map<Element, Element[]>();
        for (const element of elements) {
            const ancestor = TableStructure.nearestAncestorNamed(element, ancestorLocalName);
            if (ancestor === null) continue;
            const group = groups.get(ancestor);
            if (group === undefined) groups.set(ancestor, [element]);
            else group.push(element);
        }
        return groups;
    }

    /** The closest ancestor whose local name is `localName`, or null when the root is reached first. */
    static nearestAncestorNamed(element: Element, localName: string): Element | null {
        for (let ancestor = element.parentNode; ancestor !== null && ancestor.nodeType === 1; ancestor = ancestor.parentNode) {
            if (elementNameWithoutPrefix(ancestor as Element) === localName) return ancestor as Element;
        }
        return null;
    }

    /**
     * WHEN a table sits inside a `w:p` (a text box) or an `mc:Fallback` copy
     *   CONSEQUENCE
     *     It is not a body table; its paragraphs are not body paragraphs either.
     */
    private static isInsideExcludedContainer(tableElement: Element, bodyRoot: Element): boolean {
        for (let ancestor = tableElement.parentNode; ancestor !== null && ancestor !== bodyRoot && ancestor.nodeType === 1; ancestor = ancestor.parentNode) {
            const ancestorName = elementNameWithoutPrefix(ancestor as Element);
            if (ancestorName === "p" || ancestorName === "Fallback") return true;
        }
        return false;
    }

    /** The first direct child element with `localName`, or null. */
    static childNamed(parent: Element, localName: string): Element | null {
        for (const child of Array.from(parent.childNodes)) {
            if (child.nodeType === 1 && elementNameWithoutPrefix(child as Element) === localName) return child as Element;
        }
        return null;
    }

    /** A `w:`-namespaced attribute in either spelling the two DOM hosts produce. */
    static attributeOf(element: Element, attributeName: string): string | null {
        return element.getAttributeNS(WORDPROCESSINGML_NS, attributeName) ?? element.getAttribute(`w:${attributeName}`);
    }

    /**
     * The integer `w:val` of a property element, or `fallback` when the
     * element is missing or its value is not a number.
     *
     * EXAMPLE
     *   <w:gridSpan w:val="2"/> -> 2
     *   null, fallback 1        -> 1
     */
    private static integerValueOf(element: Element | null, fallback: number): number {
        if (element === null) return fallback;
        const parsed = Number.parseInt(TableStructure.attributeOf(element, "val") ?? "", 10);
        return Number.isFinite(parsed) ? parsed : fallback;
    }
}
